import type { ChatApiConfig, ChatMenuOptions, ChatOptions, ChatPrompt } from "./types";
/*
This is shared between V2 and V3
*/
function htmlEncode(input: string) {
	return input
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function createChatUI(prompts: ChatPrompt[], options: ChatOptions) {
	const chatUI = document.createElement("div");
	chatUI.id = "chat";
	chatUI.classList.add("v3-chat", "v3-dialog__content");
	chatUI.dataset.options = JSON.stringify(options);
	let content =
		`${options.thinkingEffort ? `<label class="v3-chat__thinking-effort">Thinking effort <select class="v3-chat__thinking-effort-select v3-input"><option value="medium">Medium</option><option value="high">High</option></select></label>` : ``}
		<div class="v3-chat__history"></div>
		<div class="v3-chat__intro">
			${options.banner ? `<div class="v3-chat__intro-logo ui-icon-openai ui-icon-only"></div><p>${htmlEncode(options.banner)}</p>` : ``}
			${prompts.map(x => { var params = new URLSearchParams(Object.entries(x.params).filter(([, value]) => value !== undefined).map(([key, value]) => [key, String(value)])).toString(); return `<div class="v3-chat__item-sample v3-chat__item"><a href="${params ? "#!?" + params : "#"}" class="v3-chat__item-text">${htmlEncode(x.text)}</a></div>`; }).join("\r\n")}
		</div>
		<form class="v3-chat__prompt">
			<fieldset class="v3-controlgroup">
				<button type="button" class="v3-controlgroup__item-first v3-button v3-chat__button v3-chat__button-clear ui-button ui-icon-only ui-icon-custom" title="New Chat"><span class="ui-icontext">New Chat</span></button>
				<div class="v3-controlgroup__item v3-chat__prompt-editor">
					<textarea id="chat-prompt" autofocus="" class="v3-chat__prompt-input v3-input-textarea v3-input" name="prompt" rows="5" required="" placeholder="What you would like to do?" title="What you would like to do?"></textarea>
					<div class="v3-chat__data-access" hidden></div>
				</div>
				<button type="submit" class="v3-controlgroup__item-last v3-button v3-chat__button v3-chat__button-send ui-button ui-icon-only ui-icon-custom" title="Send"><span class="ui-icontext">Send</span></button>
			</fieldset>
		</form>`;

	if (options.allowsaved)
		content += `<ul class="v3-chat__saved-items"></ul>`;
	chatUI.innerHTML = content;
	return chatUI;
}

function getViewChatPrompts(context: ParentNode, apiConfig: ChatApiConfig): ChatMenuOptions {
	const prompts: ChatPrompt[] = [];
	let onlyViewId: number | undefined;
	let countViews = 0;
	const table = Number(apiConfig.currentTableId);
	prompts.push({ text: "Suggest a new view for this table.", params: { table } });
	const viewClassMarker = "v3-view-";
	context.querySelectorAll(`form.v3-form[class*="${viewClassMarker}"]`).forEach(f => {
		let viewId = 0;
		f.classList.forEach(c => c.startsWith(viewClassMarker) && (viewId = +c.substring(viewClassMarker.length)));
		if (viewId > 0) {
			onlyViewId ||= viewId;
			countViews++;
			const title = f.querySelector(".v3-layout-view__title")?.textContent;
			prompts.push({ text: `Ready to refine the "${title}" view.`, params: { table, view: viewId } });
		}
	});
	return { chat: "viewchat", prompts, params: { table, view: countViews === 1 ? onlyViewId : undefined } };
}

function getFormChatPrompts(context: ParentNode, apiConfig: ChatApiConfig): ChatMenuOptions | undefined {
	const prompts: ChatPrompt[] = [];
	const table = Number(apiConfig.currentTableId);
	const previewLayout = context.querySelector("#frm_preview .v3-gridlayout");
	const editLayout = context.querySelector("#frm_edit .v3-gridlayout");
	if (previewLayout || editLayout) {
		const qs = new URLSearchParams(location.search);
		const params = {
			table,
			storeKey: table,
			mode: document.location.pathname.endsWith("/preview.aspx") ? "preview" : qs.has("b") ? "customButton" : "edit",
			id: qs.get("id"),
			b: qs.has("b") ? qs.get("b") : undefined
		};
		prompts.push({ text: "Review the form layout for this table.", params });
		prompts.push({ text: "Review the form behavior configuration for this table.", params });
		return { chat: "formchat", storeKey: table, prompts, params };
	}
}

function getSetupChatPrompts(): ChatMenuOptions {
	const chat = (Number(location.pathname.match(/\/db\/(?:(\d+)(?:-\d+)?)\//)?.[1]) & 1) ? "setupxmlchat" : "setupchat";
	// OLD SIDEBAR!
	const tableLink = document.querySelector("#td-tablelist > a.ui-selected");
	const table = tableLink?.getAttribute("href")?.match(/tableoverview\.aspx\?table=(\d+)/i)?.[1];
	return { chat, thinkingEffort: true, banner: "Ask me anything about your database setup.", allowsaved: true, prompts: [], params: { table } };
}

function getDefaultChatPrompts(_context: ParentNode, apiConfig: ChatApiConfig): ChatMenuOptions {
	const table = Number(apiConfig.currentTableId);
	return { chat: "viewchat", prompts: [], params: { table } };
}

function getChatPrompts(context: ParentNode, apiConfig: ChatApiConfig): ChatMenuOptions {
	const path= document.location.pathname.toLowerCase();
	let result;
	if (path.includes("/setup/")) {
		result = getSetupChatPrompts();
	} else if (path.endsWith("/preview.aspx") || path.endsWith("/edit.aspx")) {
		result = getFormChatPrompts(context, apiConfig);
	} else if (path.endsWith("/view.aspx") || path.endsWith("/overview.aspx")) {
		result = getViewChatPrompts(context, apiConfig);
	}
	return result ?? getDefaultChatPrompts(context, apiConfig);
}

function initChatButton(callback: (title: string, apiConfig: ChatApiConfig) => void) {
	var airole = Number(document.documentElement.dataset.allowAi);
	if (!airole)
		return;
	var apiConfig = window.gApiConfig ?? JSON.parse(document.documentElement.dataset.apiConfig ?? "null");
	const path = document.location.pathname;
	const page = path.substring(path.lastIndexOf("/") + 1).toLowerCase();
	if (path.includes("/setup/")) {
		callback("Ask a question", apiConfig);
	} else if ((page == "preview.aspx" || page == "edit.aspx") && (airole & 0x0400)) {
		callback("Form Designer", apiConfig);
	} else if ((page == "view.aspx" || page == "overview.aspx") && (airole & 0x0100)) {
		callback("View Designer", apiConfig);
	}
}

function getAuditChatOptions(href: string | null): ChatMenuOptions | null {
	if (!href?.startsWith("#ai-conversation?")) return null;
	const params = new URLSearchParams(href.substring("#ai-conversation?".length));
	const chat = params.get("chat");
	const threadId = params.get("threadId");
	if (!chat || !threadId || !["mainagent", "setupchat", "setupxmlchat"].includes(chat) ||
		!/^thread_[A-Za-z0-9_=-]+$/.test(threadId ?? "") || threadId.length > 128) return null;
	return { chat, threadId, noStore: true, allowsaved: true, prompts: [], params: {} };
}

function initAuditChatLinks(context: ParentNode, openChat: (options: ChatMenuOptions) => void) {
	context.querySelectorAll<HTMLAnchorElement>("a.js-ai-conversation").forEach(link => {
		link.onclick = event => {
			const options = getAuditChatOptions(link.getAttribute("href"));
			if (!options) return;
			event.preventDefault();
			openChat(options);
		};
	});
}

export { initChatButton, getChatPrompts, createChatUI, getAuditChatOptions, initAuditChatLinks };
